import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';

import { AccessDenied } from 'components/QuestionFlow/AccessDenied';
import { InvalidFlow } from 'components/QuestionFlow/Invalidflow';
import QuestionFlow from 'components/QuestionFlow/QuestionFlow';
import QuizFlow from 'components/QuestionFlow/QuizFlow';
import VoiceFlow from 'components/QuestionFlow/VoiceFlow';
import RoleplayPractice from 'pages/RoleplayPractice/RoleplayPractice';
import { useAuth } from 'contexts/AuthContext';
import { useCachedFlowCompletionStatus } from 'hooks/useCachedFlowCompletionStatus';
import { QuestionFlowLoadingState } from 'components/QuestionFlow/QuestionFlowLoadingState';
import { DAILY_CHECKIN_FLOW_NAME, INTAKE_ASSESSMENT_FLOW_NAME } from 'constants/flows';
import { FlowEntryBootstrapResult, bootstrapFlowEntry } from './flowEntryBootstrap';
import { opensInVoiceMode } from './flowEntryMode';
import { useFlowThemeTransitionReady } from 'components/QuestionFlow/themes/FlowThemeTransition';
import { quiz } from 'services/api';
import type { Flow, QuizStartResponse, VoiceSessionContext } from 'types';

const VoiceQuizEntry: React.FC<{ flow: Flow; flowName: string; courseId: number | null }> = ({ flow, flowName, courseId }) => {
  const [context, setContext] = useState<VoiceSessionContext | null>(null);
  const [error, setError] = useState<string | null>(null);
  const startRequestRef = useRef<Promise<{ data: QuizStartResponse }> | null>(null);
  useEffect(() => {
    let cancelled = false;
    startRequestRef.current ??= quiz.startQuiz(flow.id);
    void startRequestRef.current.then(({ data }) => {
      if (!cancelled) setContext({ kind: 'quiz_attempt', id: data.attempt_id });
    }).catch(() => { if (!cancelled) setError('Unable to start the quiz conversation. Your existing attempt is safe.'); });
    return () => { cancelled = true; };
  }, [flow.id]);
  useFlowThemeTransitionReady(Boolean(error), { unthemed: true });
  if (error) return <AccessDenied message={error} />;
  if (!context) return <QuestionFlowLoadingState />;
  return <VoiceFlow flowName={flowName} courseId={courseId} context={context} initialFlow={flow} />;
};

export const FlowRoute: React.FC = () => {
  const { flowName, courseId: courseIdParam } = useParams<{ flowName: string; courseId?: string }>();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  const [bootstrapResult, setBootstrapResult] = useState<FlowEntryBootstrapResult | null>(null);
  const [retryToken, setRetryToken] = useState(0);
  const [bootstrapLoading, setBootstrapLoading] = useState<boolean>(true);
  const [bootstrapResolvedKey, setBootstrapResolvedKey] = useState<string | null>(null);

  // Re-use the cached intake status already fetched by ProtectedRoute's useAuthWithIntakeStatus.
  const { completionStatus, loading: intakeStatusLoading, error: intakeStatusError } =
    useCachedFlowCompletionStatus(
      INTAKE_ASSESSMENT_FLOW_NAME,
      true,
      isAuthenticated,
      user?.id != null ? String(user.id) : undefined,
      'system-intake',
    );

  const hasCompletedIntake = Boolean(
    completionStatus?.completed_at ?? user?.has_completed_intake ?? false,
  );

  const targetDate = searchParams.get('targetDate');
  const requestedMode = searchParams.get('mode');
  const isVoiceModeRequested = requestedMode === 'voice';
  const isStandardModeRequested = requestedMode === 'standard';
  const flowRunIdParam = searchParams.get('flowRunId');
  const flowRunIdRaw = flowRunIdParam != null ? Number(flowRunIdParam) : null;
  const flowRunId = flowRunIdRaw !== null && !Number.isNaN(flowRunIdRaw) ? flowRunIdRaw : null;
  const legacyCourseIdParam = searchParams.get('courseId');
  const courseIdRaw = courseIdParam != null ? Number(courseIdParam) : legacyCourseIdParam != null ? Number(legacyCourseIdParam) : null;
  const courseId = courseIdRaw !== null && !Number.isNaN(courseIdRaw) ? courseIdRaw : null;
  const bootstrapRequestKey = JSON.stringify([
    flowName ?? '',
    courseId,
    targetDate,
    flowRunId,
    requestedMode,
  ]);

  useEffect(() => {
    if (flowName === DAILY_CHECKIN_FLOW_NAME) {
      setBootstrapLoading(false);
      return;
    }

    if (intakeStatusLoading && !intakeStatusError) {
      return;
    }

    if (!flowName) {
      setBootstrapResult({
        status: 'error',
        message: 'Module not found.',
      });
      setBootstrapResolvedKey(bootstrapRequestKey);
      setBootstrapLoading(false);
      return;
    }

    let isCancelled = false;

    const loadBootstrap = async () => {
      setBootstrapLoading(true);
      setBootstrapResult(null);
      setBootstrapResolvedKey(null);

      try {
        const result = await bootstrapFlowEntry({
          flowName,
          courseId,
          targetDate,
          flowRunId,
          bypassPrerequisiteCheck: Boolean(user?.is_staff),
          requestedMode,
        });

        if (isCancelled) {
          return;
        }
        setBootstrapResult(result);
        setBootstrapResolvedKey(bootstrapRequestKey);
      } finally {
        if (!isCancelled) {
          setBootstrapLoading(false);
        }
      }
    };

    void loadBootstrap();

    return () => {
      isCancelled = true;
    };
  }, [bootstrapRequestKey, courseId, flowName, flowRunId, intakeStatusError, intakeStatusLoading, requestedMode, targetDate, user?.is_staff, retryToken]);

  const invalidRoleplayRun = bootstrapResult?.status === 'ready' && bootstrapResult.kind === 'roleplay'
    && typeof (bootstrapResult.bootstrap.flowRun.flow_run_id ?? bootstrapResult.bootstrap.flowRun.id) !== 'number';
  useFlowThemeTransitionReady(
    !bootstrapLoading && bootstrapResolvedKey === bootstrapRequestKey
      && (bootstrapResult?.status === 'error' || bootstrapResult?.status === 'access_denied'
        || bootstrapResult?.status === 'blocked' || invalidRoleplayRun),
    { unthemed: true },
  );

  if (flowName === DAILY_CHECKIN_FLOW_NAME) {
    return <Navigate to="/home" replace />;
  }

  if (intakeStatusLoading && !intakeStatusError) {
    return <QuestionFlowLoadingState />;
  }

  if (
    flowName === INTAKE_ASSESSMENT_FLOW_NAME
    && hasCompletedIntake
    && !user?.is_staff
    && !intakeStatusError
  ) {
    return <Navigate to="/home" replace />;
  }

  if (bootstrapLoading || !bootstrapResult || bootstrapResolvedKey !== bootstrapRequestKey) {
    return <QuestionFlowLoadingState />;
  }

  if (bootstrapResult.status === 'course_locked') {
    return <Navigate to={`/training?courseId=${bootstrapResult.courseId}#courses`} replace state={{ courseLocked: true }} />;
  }

  if (bootstrapResult.status === 'blocked') {
    return <InvalidFlow flow={bootstrapResult.blockingFlow} courseId={courseId} />;
  }

  if (bootstrapResult.status === 'access_denied' || bootstrapResult.status === 'error') {
    return <AccessDenied message={bootstrapResult.message} courseId={courseId} onRetry={bootstrapResult.status === 'error' ? () => setRetryToken((value) => value + 1) : undefined} />;
  }

  if (bootstrapResult.status === 'redirect_to_results') {
    return (
      <Navigate
        to={`/quiz-results/${bootstrapResult.flow.id}${typeof courseId === 'number' ? `?courseId=${courseId}` : ''}`}
        replace
        state={{ flowName: bootstrapResult.flow.name, courseId }}
      />
    );
  }

  if (bootstrapResult.kind === 'quiz') {
    if (isVoiceModeRequested && bootstrapResult.bootstrap.flow.voice_mode_available) {
      return <VoiceQuizEntry key={bootstrapResult.bootstrap.flow.id} flow={bootstrapResult.bootstrap.flow} flowName={flowName || ''} courseId={courseId} />;
    }
    return (
      <QuizFlow
        flowName={flowName || ''}
        courseId={courseId}
        initialBootstrap={bootstrapResult.bootstrap}
      />
    );
  }

  if (bootstrapResult.kind === 'roleplay') {
    const roleplayFlowRun = bootstrapResult.bootstrap.flowRun;
    const roleplayFlowRunId = roleplayFlowRun.flow_run_id ?? roleplayFlowRun.id ?? null;
    if (typeof roleplayFlowRunId !== 'number') {
      return <AccessDenied message="Unable to start this roleplay practice." />;
    }
    const restoreVoice = !requestedMode && roleplayFlowRun.last_interaction_mode === 'voice'
      && roleplayFlowRun.status === 'in_progress';
    if (!isStandardModeRequested && (isVoiceModeRequested || restoreVoice) && bootstrapResult.bootstrap.flow.voice_mode_available) {
      return <VoiceFlow flowName={flowName || ''} flowRunId={roleplayFlowRunId}
        courseId={courseId} initialFlow={bootstrapResult.bootstrap.flow} />;
    }
    return (
      <RoleplayPractice
        flowName={flowName || ''}
        flowRunId={roleplayFlowRunId}
        courseId={courseId}
        voiceModeAvailable={bootstrapResult.bootstrap.flow.voice_mode_available}
      />
    );
  }

  const voiceModeUnavailableMessage = isVoiceModeRequested
    ? bootstrapResult.bootstrap.flow.voice_mode_block_reason ?? 'Voice mode is unavailable for this module.'
    : null;
  const bootstrapFlowRun = bootstrapResult.bootstrap.flowRun;
  const restoredFlowRunId =
    typeof bootstrapFlowRun?.flow_run_id === 'number'
      ? bootstrapFlowRun.flow_run_id
      : typeof bootstrapFlowRun?.id === 'number'
        ? bootstrapFlowRun.id
        : flowRunId;

  if (opensInVoiceMode(bootstrapResult.bootstrap.flow, bootstrapFlowRun, requestedMode)) {
    return (
      <VoiceFlow
        flowName={flowName || ''}
        courseId={courseId}
        targetDate={targetDate}
        flowRunId={restoredFlowRunId}
        initialBootstrap={bootstrapResult.bootstrap}
      />
    );
  }

  return (
    <QuestionFlow
      flowName={flowName || ''}
      courseId={courseId}
      targetDate={targetDate}
      flowRunId={flowRunId}
      initialBootstrap={bootstrapResult.bootstrap}
      voiceEntryNotice={voiceModeUnavailableMessage}
    />
  );
};
